import {Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, inject, model, SecurityContext, viewChild} from '@angular/core';
import {MarkdownRendererComponent} from '../markdown-renderer/markdown-renderer.component';
import {AppFormat} from '../../lib/format';
import {DomSanitizer} from '@angular/platform-browser';
import {Alerts, DialogButton} from '../../lib/alerts';
import {DynamicComponentBase} from '../../service/ng-dynamic-components.service';
import {AuthService} from '../../security/auth.service';

export interface EditableComment {
    id?: string;
    createdBy: string;
    createdAt: Date;
    content: string;
    editMode?: boolean;
}

@Component({
    selector: 'app-cell-comment-editor',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [],
    templateUrl: './cell-comment-editor.component.html',
    styleUrl: './cell-comment-editor.component.scss',
})
export class CellCommentEditorComponent extends DynamicComponentBase {

    readonly comments = model<EditableComment[]>([])

    protected AppFormat = AppFormat;
    private sanitizer = inject(DomSanitizer);
    private readonly authService = inject(AuthService);

    private readonly el = inject(ElementRef);
    private readonly newCommentTextArea = viewChild.required<ElementRef<HTMLTextAreaElement>>('newComment');

    constructor() {
        super();
    }

    addComment(comment: EditableComment) {
        this.comments.update(comments => [...comments, comment]);
    }

    protected renderMarkdown(markdown: string): string | null {
        const html = MarkdownRendererComponent.syncParseAndPurify(markdown);
        return this.sanitizer.sanitize(SecurityContext.HTML, html);
    }

    protected toggleEdit(comment: EditableComment) {
        comment.editMode = !comment.editMode;
        this.adjustFocus();
    }

    protected async deleteComment(comment: EditableComment) {
        console.log('deleteComment', comment);
        const btn = await Alerts.alertPopup({
            owner: this.el.nativeElement,
            body: 'Are you sure you want to delete this comment?',
            buttons: [DialogButton.Yes, DialogButton.No],
        });
        if (btn === DialogButton.Yes) {
            this.comments.update(comments => comments.filter(c => c !== comment));
        }
        this.adjustFocus();
    }

    protected saveEditedText(commentContainer: HTMLDivElement, comment: EditableComment) {
        const textArea = commentContainer.querySelector('textarea') as HTMLTextAreaElement;
        comment.content = textArea.value;
        comment.editMode = false;
        this.adjustFocus();
    }

    protected onSendNewComment(textArea: HTMLTextAreaElement) {
        const newComment: EditableComment = {
            createdBy: this.authService.username ?? 'unknown',
            createdAt: new Date(),
            content: textArea.value
        };
        this.addComment(newComment);
        textArea.value = '';
        textArea.style.height = '20px';
    }

    protected adjustTextareaHeight(textArea: HTMLTextAreaElement) {
        const newLineCount = textArea.value.split('\n').length;
        const newHeight = Math.max(newLineCount * 20, 20);
        textArea.style.height = newHeight + 'px';
    }

    // move the focus to the component to prevent it from automatically
    // being closed when it's in a Wijmo popup with hide-on-blur
    private adjustFocus() {
        const textArea = this.newCommentTextArea()!.nativeElement;
        textArea.focus();
    }
}
